import type { PortfolioProjectItem } from '../../firebase/types/portfolio.types';
import { lines } from './model';
import { CoverImage, LiveLink, ResultPill, UnavailableNotice } from './projectBits';
import { Band, SectionIndex } from './ui';

function techParts(tech?: string): string[] | null {
  const value = tech?.trim();
  if (!value) return null;
  const parts = value.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length > 1 && parts.every((part) => part.length <= 36)) return parts;
  return null;
}

function ProjectCard({ project, indexLabel }: { project: PortfolioProjectItem; indexLabel: string }) {
  const highlights = lines(project.highlights);
  const chips = techParts(project.tech);
  const cover = project.screenshotUrl?.trim() || project.imageUrl?.trim();
  const linkLabel = project.isNotWorking ? 'View project (may be down)' : 'View project';

  return (
    <article className="panel flex h-full flex-col overflow-hidden">
      <CoverImage src={cover} alt={project.name} />
      <div className="flex h-full flex-col p-4 md:p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-xs font-medium text-signal">{indexLabel}</span>
        </div>
        <h3 className="text-lg font-semibold tracking-tight md:text-xl">{project.name}</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <ResultPill value={project.resultHighlight} />
        </div>
        <div className="mt-3">
          <UnavailableNotice active={project.isNotWorking} />
        </div>
        {chips ? (
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {chips.map((chip) => (
              <li key={chip} className="chip">{chip}</li>
            ))}
          </ul>
        ) : project.tech ? (
          <p className="mt-2 text-sm leading-relaxed text-faint">{project.tech}</p>
        ) : null}
        {highlights.length ? (
          <ul className="mt-3 space-y-1.5 text-sm leading-relaxed text-muted">
            {highlights.map((highlight, i) => (
              <li key={i}>{highlight}</li>
            ))}
          </ul>
        ) : null}
        <div className="mt-auto pt-4">
          <LiveLink href={project.url} label={linkLabel} />
        </div>
      </div>
    </article>
  );
}

export default function ProjectsSection({
  projects,
  index,
}: {
  projects: PortfolioProjectItem[];
  index: string;
}) {
  return (
    <Band id="projects">
      <SectionIndex index={index} title="Projects" />
      <div className="grid gap-3 md:grid-cols-2">
        {projects.map((project, i) => (
          <ProjectCard
            key={`${project.name}-${i}`}
            project={project}
            indexLabel={String(i + 1).padStart(2, '0')}
          />
        ))}
      </div>
    </Band>
  );
}
